
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { ContentStats } from './AdminContentDashboard';

interface UsersOverviewProps {
  loading: boolean;
  stats?: ContentStats['users'];
}

const COLORS = ['#8884d8', '#82ca9d', '#ffc658'];

const UsersOverview: React.FC<UsersOverviewProps> = ({ loading, stats }) => {
  // Prepare data for user type distribution
  const userTypeData = stats ? [
    { name: 'Authors', value: stats.authors },
    { name: 'Non-Authors', value: stats.total - stats.authors }
  ] : [];

  // Prepare data for monthly trend
  const monthlyData = stats?.byMonth ? 
    Object.entries(stats.byMonth)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, count]) => ({ date, count })) : [];

  const authorPercentage = stats ? 
    ((stats.authors / (stats.total || 1)) * 100).toFixed(1) : '0';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="col-span-1 w-full">
          <CardHeader>
            <CardTitle className="text-lg">User Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={userTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {userTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <p className="text-center text-sm text-muted-foreground mt-4">
                  {authorPercentage}% of users have published at least one paper
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* User Growth Over Time */}
        <Card className="col-span-1 md:col-span-2 w-full">
          <CardHeader>
            <CardTitle className="text-lg">User Growth Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" name="New Users" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UsersOverview;
